import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ACTION_ROW_CLASS, ActionRowBody, ActionRowChevron } from '@/components/action-items-table'
import { ManufacturerAssignmentPicker } from '@/components/manufacturer-assignment-picker'
import { formatApiErrorMessage } from '@/lib/api-error'
import { useAssignDataActionManufacturer } from './hooks'
import { MailboxBlockItem, SENDER_BLOCKED } from './mailbox-block-item'
import type { ManufacturerDataActionGroup } from './model'

type GroupAction = ManufacturerDataActionGroup['actions'][number]

/** An email from an unknown sender: staff pick its manufacturer, and the agent then reads it. */
function MatchManufacturer({ action }: { action: GroupAction }) {
  const assign = useAssignDataActionManufacturer()
  return (
    <ManufacturerAssignmentPicker
      currentName="Match manufacturer"
      pending={assign.isPending}
      className="-ml-2 text-xs"
      onChange={(manufacturer) =>
        assign.mutate(
          { actionId: action.id, manufacturerUuid: manufacturer.uuid },
          {
            onSuccess: () => toast.success(`Matched to ${manufacturer.name}`),
            onError: (error) => toast.error(formatApiErrorMessage('Not matched', error)),
          },
        )
      }
    />
  )
}

function GroupRows({ group }: { group: ManufacturerDataActionGroup }) {
  const manufacturer = group.manufacturer
  return (
    <ul
      aria-label={manufacturer ? `Action items for ${manufacturer.name}` : groupTitle(group)}
      className="divide-y overflow-hidden rounded-lg border bg-background"
    >
      {group.actions.map((action) =>
        action.action_type === SENDER_BLOCKED ? (
          <li key={action.uuid} className="p-3">
            <MailboxBlockItem actionId={action.id} bordered={false} />
          </li>
        ) : manufacturer ? (
          <li key={action.uuid} className={ACTION_ROW_CLASS}>
            {/* The group names the manufacturer; its rows don't repeat it. "" makes the row a link. */}
            <ActionRowBody action={{ ...action, manufacturer_name: null }} projectUuid="" />
            <ActionRowChevron />
          </li>
        ) : (
          <li key={action.uuid} className={ACTION_ROW_CLASS}>
            <ActionRowBody action={action} />
            <div className="col-start-2 col-end-4 mt-1">
              <MatchManufacturer action={action} />
            </div>
          </li>
        ),
      )}
    </ul>
  )
}

/** A group with no manufacturer: our own mailboxes that can't send (USA-1729), or unmatched emails. */
function groupTitle(group: ManufacturerDataActionGroup) {
  return group.actions.every((action) => action.action_type === SENDER_BLOCKED) ? "Email can't send" : 'Unmatched emails'
}

/** The no-manufacturer group splits in two, mailboxes that can't send first: every Data email waits on them. */
function splitGroups(items: ManufacturerDataActionGroup[]): ManufacturerDataActionGroup[] {
  return items.flatMap((group) => {
    if (group.manufacturer) return [group]
    const blocked = group.actions.filter((action) => action.action_type === SENDER_BLOCKED)
    const unmatched = group.actions.filter((action) => action.action_type !== SENDER_BLOCKED)
    return [blocked, unmatched]
      .filter((actions) => actions.length > 0)
      .map((actions) => ({ ...group, actions }))
  })
}

function GroupName({ group }: { group: ManufacturerDataActionGroup }) {
  if (!group.manufacturer) return <p className="font-medium">{groupTitle(group)}</p>
  return (
    <Link
      to="/manufacturers/$manuId"
      params={{ manuId: group.manufacturer.uuid }}
      search={{ tab: 'emails' }}
      className="font-medium text-primary hover:underline"
    >
      {group.manufacturer.name}
    </Link>
  )
}

function groupKey(group: ManufacturerDataActionGroup) {
  return group.manufacturer?.uuid ?? groupTitle(group)
}

/** The Data role's items on the Action Items page (USA-1474): one row per manufacturer, unmatched emails first. */
export function DataActionGroups({ items: listed }: { items: ManufacturerDataActionGroup[] }) {
  const items = splitGroups(listed)
  return (
    <>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[38%]">Manufacturer</TableHead>
              <TableHead>Action Items</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((group) => (
              <TableRow key={groupKey(group)} className="align-top hover:bg-transparent">
                <TableCell className="align-top whitespace-normal">
                  <GroupName group={group} />
                </TableCell>
                <TableCell className="align-top whitespace-normal">
                  <GroupRows group={group} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-3 lg:hidden">
        {items.map((group) => (
          <div key={groupKey(group)} className="space-y-3 rounded-lg border bg-card p-3">
            <GroupName group={group} />
            <GroupRows group={group} />
          </div>
        ))}
      </div>
    </>
  )
}
